import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants';

// Create axios instance with array buffer response type to prevent any automatic parsing
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Use 'arraybuffer' then convert to text to have full control over parsing
  responseType: 'arraybuffer',
  transformResponse: [
    function (data) {
      try {
        // Convert ArrayBuffer to string
        const text = new TextDecoder('utf-8').decode(data);
        
        // Check if it's HTML before trying to parse as JSON
        const trimmedText = text.trim();
        if (trimmedText.startsWith('<!') || trimmedText.startsWith('<html') || trimmedText.startsWith('<?xml')) {
          // Create a custom error object that won't trigger console errors
          const error = new Error('Server returned HTML instead of JSON');
          error.name = 'HTMLResponseError';
          error.isHtmlResponse = true;
          error.htmlContent = trimmedText.substring(0, 200);
          // Store the error in a way that won't cause parsing issues
          error.responseData = { error: 'HTML_RESPONSE', message: 'Backend API may not be running or endpoint does not exist' };
          throw error;
        }
        
        // Try to parse as JSON
        try {
          const parsed = JSON.parse(text);
          return parsed;
        } catch (parseError) {
          // If parsing fails, check if it's HTML that didn't start with <!
          if (trimmedText.includes('<') && trimmedText.includes('>')) {
            const error = new Error('Server returned HTML instead of JSON');
            error.name = 'HTMLResponseError';
            error.isHtmlResponse = true;
            error.htmlContent = trimmedText.substring(0, 200);
            error.responseData = { error: 'HTML_RESPONSE', message: 'Backend API may not be running' };
            throw error;
          }
          
          // Check if it's the specific "Unexpected token" error
          if (parseError.message && parseError.message.includes('Unexpected token')) {
            const error = new Error('Invalid JSON response from server');
            error.name = 'JSONParseError';
            error.originalError = parseError;
            error.responseData = { error: 'JSON_PARSE_ERROR', message: 'Server returned invalid JSON' };
            throw error;
          }
          
          // Otherwise, it's a general JSON parsing error
          const error = new Error('Invalid JSON response from server');
          error.name = 'JSONParseError';
          error.originalError = parseError;
          error.responseData = { error: 'JSON_PARSE_ERROR', message: `Invalid response: ${trimmedText.substring(0, 100)}` };
          throw error;
        }
      } catch (error) {
        // Re-throw the error so axios interceptors can handle it
        throw error;
      }
    }
  ],
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    // Response is already parsed by transformResponse, so just return it
    return response;
  },
  (error) => {
    // Handle HTML response errors (caught in transformResponse)
    if (error.isHtmlResponse) {
      console.error('HTML Response Error:', error.message);
      console.error('Possible causes:');
      console.error('1. Backend server is not running');
      console.error('2. Wrong API base URL (check REACT_APP_API_BASE_URL)');
      console.error('3. API endpoint does not exist');
      console.error('4. CORS or network issue');
      if (error.htmlContent) {
        console.error('HTML content preview:', error.htmlContent);
      }
      // Return a user-friendly error
      const userError = new Error('Cannot connect to backend API. Please ensure the server is running on the correct port.');
      userError.isHtmlResponse = true;
      return Promise.reject(userError);
    }
    
    // Handle JSON parsing errors that weren't caught
    if (error.message && error.message.includes('Unexpected token')) {
      console.error('JSON Parse Error detected:', error.message);
      const betterError = new Error('Server returned invalid response. Please check if the backend API is running and accessible.');
      betterError.originalError = error;
      return Promise.reject(betterError);
    }
    
    // Handle network errors or other responses
    if (error.response) {
      // Try to get the response data if it exists
      let responseData = null;
      try {
        // response.data should already be parsed by transformResponse
        if (error.response.data && typeof error.response.data === 'object') {
          responseData = error.response.data;
        }
      } catch (e) {
        // Ignore parsing errors here
      }
      
      if (error.response.status === 401) {
        // Unauthorized - but don't redirect if this is a login attempt
        const requestUrl = error.config?.url || '';
        const isLoginRequest = requestUrl.includes('/auth/login') || 
                              requestUrl === '/auth/login' ||
                              requestUrl.endsWith('/auth/login');
        
        if (!isLoginRequest) {
          // Only redirect if it's not a login request
          // Clear token and redirect to login
          localStorage.removeItem(STORAGE_KEYS.TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER);
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        // For login requests, preserve the error message from the backend
        // and let it propagate so the login form can handle it
        if (responseData?.message) {
          error.message = responseData.message;
        } else if (responseData?.error) {
          error.message = responseData.error;
        }
        return Promise.reject(error);
      }
      
      // For other errors, attach the response message if available
      if (responseData) {
        if (responseData.message) {
          error.message = responseData.message;
        } else if (responseData.error) {
          error.message = responseData.error;
        }
        // Attach full response data for components to use
        error.responseData = responseData;
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received from server. Is the backend running?');
      const networkError = new Error('Cannot connect to server. Please check if the backend is running on the correct port.');
      networkError.isNetworkError = true;
      return Promise.reject(networkError);
    }
    
    return Promise.reject(error);
  }
);

export default api;
